import React from 'react';
import { usePublicWebSocket } from '../../hooks/usePublicWebSocket';

export const FullTimelineLayout = ({ matchId }) => {
  const {
    connected,
    joined,
    events,
    score,
    viewers,
    match,
    clock
  } = usePublicWebSocket(matchId);

  // Filter events by team
  const homeEvents = match ? events.filter(e => e.team_id === match.home_team_id) : [];
  const awayEvents = match ? events.filter(e => e.team_id === match.away_team_id) : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold text-white mb-4">Live Match</h1>
          <div className="flex justify-center gap-4">
            <span className={`px-4 py-2 rounded-full text-sm font-medium ${
              connected ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
            }`}>
              {connected ? '🟢 LIVE' : '🔴 OFFLINE'}
            </span>
            <span className="px-4 py-2 rounded-full bg-white bg-opacity-20 text-white text-sm font-medium">
              👥 {viewers} watching
            </span>
          </div>
        </div>

        {/* Scoreboard */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 mb-8">
          {/* Clock Display */}
          <div className="text-center mb-6">
            <div className="inline-block bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-xl">
              <div className="text-5xl font-mono font-black">
                {String(clock.minute).padStart(2, '0')}:{String(clock.second).padStart(2, '0')}
                {clock.injuryTime > 0 && clock.minute >= 45 && (
                  <span className="text-3xl ml-2">+{clock.injuryTime}</span>
                )}
              </div>
              <div className="text-sm mt-1 font-semibold opacity-90">
                {clock.half === 1 && '1ST HALF'}
                {clock.half === 2 && '2ND HALF'}
                {clock.half === 3 && 'EXTRA TIME 1ST'}
                {clock.half === 4 && 'EXTRA TIME 2ND'}
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center">
            {/* Home Team */}
            <div className="flex-1 text-center">
              <div className="text-6xl font-bold text-blue-600 mb-2">{score.home}</div>
              <div className="text-xl font-semibold text-gray-700">
                {match?.home_team?.name || 'Home'}
              </div>
            </div>

            {/* VS Separator */}
            <div className="px-8">
              <div className="text-4xl font-bold text-gray-400">VS</div>
            </div>

            {/* Away Team */}
            <div className="flex-1 text-center">
              <div className="text-6xl font-bold text-green-600 mb-2">{score.away}</div>
              <div className="text-xl font-semibold text-gray-700">
                {match?.away_team?.name || 'Away'}
              </div>
            </div>
          </div>
        </div>

        {/* Events Timeline - Two Columns */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <span className="mr-3">📋</span>
            Match Events
            <span className="ml-3 text-sm font-normal text-gray-500">({events.length})</span>
          </h2>

          {!joined && (
            <div className="text-center py-8 text-gray-500">
              Connecting to match...
            </div>
          )}

          {joined && events.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <div className="text-6xl mb-4">⚽</div>
              <p className="text-lg">No events yet. Match starting soon...</p>
            </div>
          )}

          {joined && events.length > 0 && (
            <div className="grid grid-cols-2 gap-6">
              {/* Home Team Events */}
              <div>
                <h3 className="text-lg font-bold text-blue-600 mb-4 pb-2 border-b-2 border-blue-200">
                  {match?.home_team?.name || 'Home'} Events
                </h3>
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {homeEvents.length === 0 ? (
                    <p className="text-center text-gray-400 py-8">No events yet</p>
                  ) : (
                    homeEvents.map((event, idx) => {
                      const getEventIcon = (type) => {
                        const eventType = type?.toLowerCase() || '';
                        if (eventType.includes('goal')) return '⚽';
                        if (eventType.includes('yellow')) return '🟨';
                        if (eventType.includes('red')) return '🟥';
                        if (eventType.includes('sub')) return '🔄';
                        return '📌';
                      };

                      const getEventColor = (type) => {
                        const eventType = type?.toLowerCase() || '';
                        if (eventType.includes('goal')) return 'bg-green-50 border-green-200';
                        if (eventType.includes('yellow')) return 'bg-yellow-50 border-yellow-200';
                        if (eventType.includes('red')) return 'bg-red-50 border-red-200';
                        if (eventType.includes('sub')) return 'bg-blue-50 border-blue-200';
                        return 'bg-gray-50 border-gray-200';
                      };

                      return (
                        <div 
                          key={event.id || idx} 
                          className={`p-3 rounded-lg border-2 ${getEventColor(event.event_type)} transition-all hover:shadow-md`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-2xl">{getEventIcon(event.event_type)}</span>
                              <div>
                                <div className="font-bold text-sm text-gray-800">
                                  {event.event_type || 'Event'}
                                </div>
                                {event.event_type?.toLowerCase().includes('sub') && event.secondary_player ? (
                                  <div className="text-xs">
                                    <div className="text-red-600 font-semibold">
                                      ↑ {event.secondary_player?.first_name} {event.secondary_player?.last_name}
                                    </div>
                                    <div className="text-green-600 font-semibold">
                                      ↓ {event.player?.first_name} {event.player?.last_name}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-xs text-gray-600">
                                    {event.player?.first_name} {event.player?.last_name}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xl font-bold text-gray-700">
                                {event.minute}'
                              </div>
                              <div className="text-xs text-gray-500">
                                H{event.half}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Away Team Events */}
              <div>
                <h3 className="text-lg font-bold text-green-600 mb-4 pb-2 border-b-2 border-green-200">
                  {match?.away_team?.name || 'Away'} Events
                </h3>
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {awayEvents.length === 0 ? (
                    <p className="text-center text-gray-400 py-8">No events yet</p>
                  ) : (
                    awayEvents.map((event, idx) => {
                      const getEventIcon = (type) => {
                        const eventType = type?.toLowerCase() || '';
                        if (eventType.includes('goal')) return '⚽';
                        if (eventType.includes('yellow')) return '🟨';
                        if (eventType.includes('red')) return '🟥';
                        if (eventType.includes('sub')) return '🔄';
                        return '📌';
                      };

                      const getEventColor = (type) => {
                        const eventType = type?.toLowerCase() || '';
                        if (eventType.includes('goal')) return 'bg-green-50 border-green-200';
                        if (eventType.includes('yellow')) return 'bg-yellow-50 border-yellow-200';
                        if (eventType.includes('red')) return 'bg-red-50 border-red-200';
                        if (eventType.includes('sub')) return 'bg-blue-50 border-blue-200';
                        return 'bg-gray-50 border-gray-200';
                      };

                      return (
                        <div 
                          key={event.id || idx} 
                          className={`p-3 rounded-lg border-2 ${getEventColor(event.event_type)} transition-all hover:shadow-md`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-2xl">{getEventIcon(event.event_type)}</span>
                              <div>
                                <div className="font-bold text-sm text-gray-800">
                                  {event.event_type || 'Event'}
                                </div>
                                {event.event_type?.toLowerCase().includes('sub') && event.secondary_player ? (
                                  <div className="text-xs">
                                    <div className="text-red-600 font-semibold">
                                      ↑ {event.secondary_player?.first_name} {event.secondary_player?.last_name}
                                    </div>
                                    <div className="text-green-600 font-semibold">
                                      ↓ {event.player?.first_name} {event.player?.last_name}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-xs text-gray-600">
                                    {event.player?.first_name} {event.player?.last_name}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xl font-bold text-gray-700">
                                {event.minute}'
                              </div>
                              <div className="text-xs text-gray-500">
                                H{event.half}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-white text-sm opacity-75">
          <p>Sports Manager • Live Match Viewer</p>
        </div>
      </div>
    </div>
  );
};

export default FullTimelineLayout;
